import { useGetBadgeDetailsQuery } from "@configs/apis/GetBadgeDetails";
import clsx from "clsx";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";

import CheckIcon from "../../assets/icons/Check";
import ChevronIcon from "../../assets/icons/Chevron";
import ClockIcon from "../../assets/icons/Clock";
import GiftVariantIcon from "../../assets/icons/GiftVariant";
import HistoryBoldIcon from "../../assets/icons/HistoryBold";
import LockIcon from "../../assets/icons/Lock";
import LockedIcon from "../../assets/icons/Locked";
import UsersIcon from "../../assets/icons/Users";
import { usePlayersCountVisibility } from "../../common/hooks/usePlayersCountVisibility";
import DetailsCard from "../../components/Cards/DetailsCard";
import ImageWithFallback from "../../components/Common/ImageWithFallback";
import Skeleton from "../../components/Common/Skeleton";
import Popup from "../../components/Popup";
import type { Progress as ProgressType } from "../../configs/apis/GetProgress/models";
import { Operator } from "../../configs/models/constants";
import {
  formatRemainingTime,
  getLargestUncompleted,
  getRewardType,
} from "../../configs/utils/utility";
import GroupsDrawer from "../ChallengeDetails/GroupsDrawer";

const BadgeDetailsPage = () => {
  const { t: translate } = useTranslation("translation");
  const { challengeId } = useParams<{ challengeId: string }>();
  const showPlayersCount = usePlayersCountVisibility();

  const [isTriggersExpanded, setIsTriggersExpanded] = useState(false);

  const [isPopupVisible, setIsPopupVisible] = useState(false);


  const {
    data: detailsData,
    isLoading,
    isFetching,
  } = useGetBadgeDetailsQuery(
    {
      challengeId: Number(challengeId)
    },
    { refetchOnMountOrArgChange: true },
  );

  const prize = detailsData?.rewards.at(0);

  if (isLoading || isFetching)
    return (
      <div className="flex min-h-[40rem] grid-cols-1 flex-col-reverse gap-8 pb-24 pt-5 md:grid md:grid-cols-2">
        <Skeleton className="h-full" />
        <Skeleton className="h-full" hasContent={false} />
      </div>
    );

  const checkCompleted = (): boolean | undefined => {
    let groupsResult: boolean[] | undefined = [];

    // loop on triggers
    groupsResult = detailsData?.groupProgress?.map((group) => {
      if (group.triggersProgress[0]?.operator === Operator.OR) {
        return group.triggersProgress.some(
          (trigger) => trigger.progress === 100,
        );
      }

      return group.triggersProgress.every(
        (trigger) => trigger.progress === 100,
      );
    });
    // loop on each group to get ultimate result
    return detailsData?.groupProgress.reduce((ultimate, currentGroup, idx) => {
      if (idx === detailsData.groupProgress.length - 1) return ultimate;

      return currentGroup?.operator === Operator.OR
        ? !!groupsResult?.some((value) => value === true)
        : !!groupsResult?.every((value) => value === true);
    }, groupsResult?.at(0));
  };

  const getHasMoreValue = (): boolean => {
    if (detailsData?.prizes.length !== 0) return false;

    if (detailsData.groupProgress.length > 1) return true;

    return !!detailsData?.groupProgress?.some((group) => {
      return group.triggersProgress.length > 1;
    });
  };

  if (!detailsData)
    return (
      <main className="flex min-h-[400px] flex-col items-center justify-center text-center">
        <HistoryBoldIcon className="text-secondTextColor/50"  />
        <p className="mb-8 text-secondTextColor">{translate("challenge_not_found")}</p>
        <Link className="text-activeColor underline" to="/">
          {translate("go_back")}
        </Link>
      </main>
    );

  return (
    <>
      <Popup
        isVisible={isPopupVisible}
        onClose={() => setIsPopupVisible(false)}
        prize={{
          value: prize?.rewardValue ?? "",
          description: prize?.rewardName ?? "",
          expirationDate: "",
          rewardType: prize?.rewardType ?? 0,
        }}
      />
      <main>
        <div>
          <h3 className="text-3xl font-semibold text-mainTextColor/80">
            {translate("badges_pager")}
          </h3>
        </div>
        <div className="flex grid-cols-1 flex-col-reverse gap-8 pb-24 pt-5 md:grid md:grid-cols-2">
          <div className="flex h-fit flex-col gap-4 rounded-xl border border-secondTextColor/40 p-4">
            <h3 className="text-xl font-semibold">{detailsData?.name}</h3>
            <div className="">
              <h4 className="font-semibold">{translate("about_badge")}</h4>
              <p className="text-secondTextColor">
                {detailsData?.description}
              </p>
            </div>

            <div className="grid w-full gap-4 text-sm md:grid-cols-2 lg:text-base">
              {detailsData?.rewards?.map((reward) => (
                <div
                  className="flex justify-between gap-2 whitespace-nowrap rounded-xl border border-secondTextColor/40 p-4 md:justify-center lg:justify-between"
                  key={reward.id}
                >
                  <span className="flex items-center gap-2 text-activeColor">
                    <GiftVariantIcon className="text-activeColor" />
                    {translate("reward")}
                  </span>
                  <p className="font-bold">
                    {getRewardType(translate, reward.rewardType)}
                  </p>
                </div>
              ))}
            </div>

            {!!detailsData?.lockedEvent && (
              <PendingSection
                isLocked={!!detailsData?.lockedEvent}
                lockedEvent={detailsData.lockedEvent}
              />
            )}

            {!detailsData?.isLocked && (
              <div className="relative flex flex-col gap-4 rounded-xl border border-secondTextColor/40 p-4 pt-8">
                <div className="absolute -top-3 left-0 flex w-full justify-center">
                  <button
                    className={clsx(
                      getHasMoreValue()
                        ? "text-activeColor"
                        : "text-secondTextColor/40",
                      "w-fit bg-mainColor px-2 flex font-semibold items-center",
                    )}
                    disabled={!getHasMoreValue()}
                    onClick={() => setIsTriggersExpanded((prev) => !prev)}
                  >
                    {translate("complete_this_group")}
                    {getHasMoreValue() && (
                      <ChevronIcon
                        className={clsx(
                          isTriggersExpanded ? "rotate-90" : "-rotate-90",
                          "h-2/3",
                        )}
                      />
                    )}{" "}
                  </button>
                </div>
                {isTriggersExpanded && (
                  <GroupsDrawer
                    groupProgress={detailsData?.groupProgress || []}
                  />
                )}
                {!isTriggersExpanded && (
                  <DetailsCard
                    isCompleted={
                      checkCompleted() || detailsData?.prizes.length !== 0
                    }
                    isMoreThanOne={getHasMoreValue()}
                    trigger={getLargestUncompleted(
                      detailsData?.groupProgress || [],
                    )}
                  />
                )}
              </div>
            )}

            {/* Gate the whole card — gating the row leaves an empty bordered box. */}
            {showPlayersCount && (
              <div className="flex flex-col divide-y divide-secondTextColor/40 rounded-xl border border-secondTextColor/40 px-4">
                <div className="flex w-full items-center justify-between py-4">
                  <span className="flex items-center gap-2 text-activeColor">
                    <UsersIcon className="text-activeColor" />
                    {translate("finish_this_challenge")}
                  </span>
                  <p className="font-bold">
                    {detailsData?.peopleFinished} {translate("player")}
                  </p>
                </div>
              </div>
            )}
          </div>
          <div>
            <div className="relative aspect-square w-full overflow-hidden rounded-2xl">
              {detailsData?.isLocked && (
                <div className="absolute flex size-full items-center justify-center rounded-xl bg-black/45">
                  <LockedIcon />
                </div>
              )}
              <div className="absolute flex size-full flex-col justify-between p-5">
                <div className="flex justify-between">
                  <span className="z-[1] flex h-fit items-center gap-1 rounded-full bg-mainColor/80 px-4 py-2 text-secondTextColor">
                    <ClockIcon /> {formatRemainingTime(detailsData?.endDate, translate)}
                  </span>
                </div>
              </div>
              <ImageWithFallback
                alt={detailsData.name}
                className={clsx(detailsData?.prizes?.length === 0 ? "grayscale" : "","size-full")}
                src={detailsData?.image}
              />
            </div>
          </div>
        </div>
      </main>
    </>
  );
};

type PendingSectionProps = {
  isLocked: boolean;
  lockedEvent: ProgressType;
};

export const PendingSection = ({
  isLocked,
  lockedEvent,
}: PendingSectionProps) => {
  const { t: translate } = useTranslation("translation");

  return (
    <div className="rounded-2xl bg-[#06418326] px-6">
      <Link
        className="my-4 flex w-full items-center justify-between gap-4"
        to={`/gamification/details/${lockedEvent.id}`}
      >
        <div className="relative flex items-center justify-center">
          {!isLocked && (
            <div className="absolute z-10 rounded-full bg-successColor p-2">
              <CheckIcon className="size-3" />
            </div>
          )}
          <LockIcon />
        </div>
        <div className="w-full text-start">
          <p className="text-lg font-semibold text-activeColor">
            {translate("locked_title")}
          </p>
          <p>{`${translate("locked_desc")} "${lockedEvent.name}" `}</p>
        </div>
        <ChevronIcon className="size-8 text-activeColor ltr:rotate-180" />
      </Link>
    </div>
  );
};

export default BadgeDetailsPage;
